import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Product, Quote } from "@/components/services/products/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, ArrowLeft, MessageSquare, Heart, Star } from "lucide-react";
import { ContactFormDialog } from "@/components/services/ContactFormDialog";
import { MessageDialog } from "@/components/services/MessageDialog";
import { PurchaseRequestDialog } from "@/components/services/products/PurchaseRequestDialog";
import { useMarketplaceFavorites } from "@/hooks/useMarketplaceFavorites";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

const MobileProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const { toggleFavorite, isFavorite } = useMarketplaceFavorites();
  const isFav = id ? isFavorite(id) : false;

  console.log("MobileProductDetails - Rendering with ID:", id);
  console.log("User session:", session?.user?.id);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      console.log("Fetching product data for ID:", id);
      try {
        const { data, error } = await supabase
          .from("professional_products")
          .select(`
            *,
            profiles:professional_id (
              full_name,
              profile_photo,
              rating,
              response_rate,
              response_time,
              total_reviews,
              about_me,
              experience,
              education,
              languages,
              certifications
            )
          `)
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error fetching product:", error);
          throw error;
        }
        console.log("Product data loaded:", data);
        
        // Parse quotes from JSON
        let parsedQuotes: Quote[] = [];
        if (data.quotes) {
          try {
            if (typeof data.quotes === 'string') {
              parsedQuotes = JSON.parse(data.quotes);
            } else if (Array.isArray(data.quotes)) {
              // Cast each item as any before accessing properties
              parsedQuotes = (data.quotes as any[]).map(q => ({
                id: q?.id || "",
                name: q?.name || "",
                price: typeof q?.price === 'number' ? q?.price : parseFloat(q?.price || '0') || 0
              }));
            } else if (typeof data.quotes === 'object') {
              // Handle Supabase JSON object format
              const quotesJson = data.quotes as any;
              if (Array.isArray(quotesJson)) {
                parsedQuotes = quotesJson.map(q => ({
                  id: q?.id || "",
                  name: q?.name || "",
                  price: typeof q?.price === 'number' ? q?.price : parseFloat(q?.price || '0') || 0
                }));
              }
            }
          } catch (e) {
            console.error("Error parsing quotes:", e);
          }
        }
        
        return {
          ...data,
          quotes: parsedQuotes
        } as Product;
      } catch (error) {
        console.error("Exception in queryFn:", error);
        throw error;
      }
    },
  });

  // Format price with the correct currency
  const formattedPrice = product ? new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: product.currency || 'EUR',
    currencyDisplay: 'symbol'
  }).format(product.price) : "";

  useEffect(() => {
    if (product?.quotes?.length > 0) {
      setSelectedQuoteId(product.quotes[0].id);
    }
  }, [product]);

  const handleToggleFavorite = async () => {
    if (id) {
      await toggleFavorite(id);
    }
  };

  const handleMessageClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour envoyer un message");
      return;
    }

    if (!product?.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening message dialog for professional:", product.professional_id);
    setIsMessageDialogOpen(true);
  };

  const handleContactClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour contacter le professionnel");
      return;
    }

    if (!product?.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening purchase dialog for product:", product.id);
    setIsPurchaseDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="icon" disabled>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Skeleton className="h-6 w-40" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg mb-4" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!product) {
    console.error("No product found for ID:", id);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <p className="text-lg font-medium">Produit non trouvé</p>
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retourner
        </Button>
      </div>
    );
  }

  // Prepare images array
  const images = product.photos?.length > 0 
    ? product.photos 
    : (product.photo_url ? [product.photo_url] : []);

  console.log("Rendering product detail with images:", images);
  console.log("Professional ID:", product.professional_id);

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-background z-10 p-4 border-b flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-medium">{product?.title}</h1>
      </div>

      <main className="space-y-6">
        {/* Images */}
        <div className="relative bg-gray-100">
          {images.length > 0 ? (
            <img 
              src={images[0]} 
              alt={product.title} 
              className="w-full aspect-square object-cover"
            />
          ) : (
            <div className="w-full aspect-square flex items-center justify-center bg-gray-200">
              <span className="text-gray-500">Aucune image disponible</span>
            </div>
          )}
          
          {/* Badge de condition */}
          <Badge className="absolute bottom-2 right-2 bg-white text-black">
            {product.condition === "new" ? "Neuf" : 
              product.condition === "like-new" ? "Comme neuf" : 
              product.condition === "good" ? "Bon état" : "Occasion"}
          </Badge>
        </div>

        {/* Product Info */}
        <div className="px-4 space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <h2 className="text-xl font-semibold">{product.title}</h2>
              <span className="text-xl font-bold text-green-600">{formattedPrice}</span>
            </div>
            <p className="text-muted-foreground">
              {product.description || "Aucune description disponible."}
            </p>

            {/* Quotes */}
            {product.quotes && product.quotes.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium mb-2">Options disponibles</h3>
                <div className="flex flex-wrap gap-2">
                  {product.quotes.map(quote => (
                    <Button
                      key={quote.id}
                      variant={selectedQuoteId === quote.id ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => setSelectedQuoteId(quote.id)}
                    >
                      {quote.name} - {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: product.currency || 'EUR'
                      }).format(quote.price)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Seller Info */}
          {product.profiles && (
            <div className="bg-background rounded-lg border p-4">
              <div className="flex items-center gap-3 mb-3">
                <Avatar>
                  <AvatarImage src={product.profiles.profile_photo || undefined} />
                  <AvatarFallback>
                    {product.profiles.full_name?.[0] || 'V'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-medium">{product.profiles.full_name}</h3>
                  {product.profiles.rating && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      <span className="text-muted-foreground">
                        {product.profiles.rating.toFixed(1)} ({product.profiles.total_reviews || 0} avis)
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {product.profiles.about_me && (
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {product.profiles.about_me}
                </p>
              )}
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-2">
            {product.address && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{product.address}</span>
              </div>
            )}
            {product.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{product.phone}</span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 flex gap-2 shadow-lg">
        <Button
          variant="outline"
          size="icon"
          onClick={handleToggleFavorite}
          className="flex-none"
        >
          <Heart className={`h-5 w-5 ${isFav ? "fill-red-500 text-red-500" : ""}`} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleMessageClick}
          className="flex-none"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
        <Button 
          className="flex-1" 
          onClick={handleContactClick}
        >
          Demander l'achat
        </Button>
      </div>

      <ContactFormDialog
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        productTitle={product?.title || ""}
        productId={product?.id || ""}
        professionalId={product?.professional_id || ""}
      />

      <MessageDialog
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
        professionalId={product?.professional_id || ""}
        professionalName={product?.profiles?.full_name || ""}
        context="product"
        itemTitle={product?.title || ""}
      />

      <PurchaseRequestDialog
        open={isPurchaseDialogOpen}
        onOpenChange={setIsPurchaseDialogOpen}
        productId={product?.id || ""}
        professionalId={product?.professional_id || ""}
        productTitle={product?.title || ""}
        productPrice={product?.price || 0}
      />
    </div>
  );
};

export default MobileProductDetails;
